import { ContractViewModel } from '@ail/contracts-api'
import { CustomerIndexViewModel, TenantViewModel } from '@ail/customers-api'
import { ApprovalProcessStepStatus, OrdersFormViewModel, OrderViewModel, Visibility } from '@ail/orders-api'
import { TFunction } from 'i18next'
import jsPDF from 'jspdf'
import autoTable from 'jspdf-autotable'
import { printDate } from '../helpers/Utils'
import { IUserViewModel } from '../types/interfaces'
import { emptyGuid } from '../types/constants'
import { Contracts } from './contracts'

export class Orders {
  public static isFormAvailable(
    form: OrdersFormViewModel,
    user?: IUserViewModel | null,
    customers?: CustomerIndexViewModel[]
  ) {
    if (!user) {
      return false
    }

    let visible = true
    switch (form.visibility) {
      case Visibility.Deleted:
        visible = false
        break
      case Visibility.Internal:
        visible = !!user.isInternalUser
        break
      case Visibility.Private:
        visible = !!user.isAdmin
        break
      default:
        visible = true
        break
    }

    const now = new Date()

    visible &&= !form.validFrom || new Date(form.validFrom) < now

    visible &&= !form.validTo || new Date(form.validTo) > now

    visible &&=
      user.isInternalUser ||
      !form.allowedForInternalUsers ||
      form.approvalProcessConfigs.some((c) => c.steps?.some((s) => user.id && s.owners?.includes(user.id)))

    visible &&=
      user.isInternalUser ||
      !form.filterCompamniesByMatchingApprovalFlows ||
      (form.ownerTenant !== emptyGuid &&
        form.approvalProcessConfigs.some((a) =>
          a.validCompanies?.some((vc) =>
            customers?.some((c) => c.customer?.tenantId === form.ownerTenant && c.customer.id === vc)
          )
        ))

    return visible || !!user.isAdmin
  }

  public static isFormAvailableForQuickAction(
    form: OrdersFormViewModel,
    user?: IUserViewModel | null,
    tenants?: TenantViewModel[],
    customers?: CustomerIndexViewModel[]
  ) {
    return (
      user?.canManageOrders &&
      this.isFormAvailable(form, user, customers) &&
      (tenants?.some((t) => t.id === form.ownerTenant) ||
        (user?.tenant && user.tenant.toLowerCase() === form.ownerTenant.toLowerCase())) &&
      (form.name.toLowerCase().includes('fuel card') || form.showInQuickActionMenuForContracts?.length)
    )
  }

  public static isFormAvailableForContractQuickAction(
    form: OrdersFormViewModel,
    contract: ContractViewModel,
    expirationDays: number,
    user?: IUserViewModel | null,
    tenants?: TenantViewModel[],
    customers?: CustomerIndexViewModel[]
  ) {
    const contractState = Contracts.getExtendedContractState(contract, expirationDays)
    const contractCustomer = customers?.find((c) => c.value && contract.owner && contract.owner.includes(c.value))
    const showForStates = form.showInQuickActionMenuForContracts?.length
      ? form.showInQuickActionMenuForContracts
      : ['Running', 'SettingUp', 'Initialized', 'Extended']
    const currentStateValid = showForStates.includes(contractState)
    const allStatesValid = showForStates.length === 1 && showForStates.at(0) === 'Default'

    return (
      this.isFormAvailableForQuickAction(form, user, tenants, customers) &&
      (!user?.isInternalUser || form.ownerTenant === contractCustomer?.customer?.tenantId) &&
      (currentStateValid || allStatesValid)
    )
  }

  public static getFormTitle(form: OrdersFormViewModel, language: string, formPageTitle?: boolean) {
    const title = form.tr?.[language] || form?.name || ''
    const splitTitle = title.split('|')
    return splitTitle.length > 0
      ? formPageTitle
        ? splitTitle[1]
          ? splitTitle[1]
          : splitTitle[0]
        : splitTitle[0]
      : title
  }

  public static getFormGenericTooltip(form: OrdersFormViewModel, language: string) {
    const en = 'en'
    const title = !!form && !!form.genericTooltip ? form.genericTooltip[language] || form.genericTooltip[en] || '' : ''
    return title
  }

  public static exportOrder(order: OrderViewModel, forms: OrdersFormViewModel[], lang: string, t: TFunction) {
    const form = forms.find((f) => f.id === order.formId)
    if (!form) {
      return
    }

    const approvalProcess = order.approvalProcess
    const data = order.data
    const steps = approvalProcess?.steps?.length ? approvalProcess.steps : approvalProcess?.stepsWithTags ?? []

    const doc = new jsPDF()

    const headCustomer = [[t('CUSTOMER'), '']]
    const headApprove = [[t('APPROVE/SIGN'), '']]
    const headApproveAdditional = [[t('STEP'), t('USER_NAME'), t('DATE'), t('NOTE')]]

    const rowsCustomer = [
      [t('COMPANY_NAME'), order.company?.companyName ?? ''],
      [t('COST_CENTER'), order.costCenter?.costCenterName ?? ''],
      [t('USER'), order.userDisplayName],
    ]

    const rowsApprove = [
      [t('ID'), order.id],
      [t('STATUS'), approvalProcess ? t(approvalProcess.status?.toUpperCase() ?? '') : t('DRAFT')],
    ]

    const rowsApproveAdditional: string[][] = []

    for (const step of steps) {
      if (step.status !== ApprovalProcessStepStatus.Pending) {
        rowsApproveAdditional.push([
          step.step.toString(),
          step.userDisplayName ?? '',
          printDate(step.timestamp, true),
          Object.values(step.notes).join('; '),
        ])
      }
    }

    doc.addImage('/interfleet4u-new2.png', 'PNG', 15, 5, 60, 12)
    doc.setFontSize(13)
    doc.text(this.getFormTitle(form, lang, true), 150, 13, {
      maxWidth: 90,
      align: 'center',
    })
    autoTable(doc, {
      columnStyles: {
        0: { cellWidth: 40, fontStyle: 'italic', lineWidth: 0.3 },
        1: { lineWidth: 0.3 },
      },
      startY: 25,
      head: headCustomer,
      body: rowsCustomer,
    })

    form.sections.forEach((section, index) => {
      if (index === 0 || (data['PL001'] === 'rad1' && index === 2)) {
        return
      }

      const headers = [[section.tr?.[lang] || section.name, '']]
      const rows: string[][] = []
      form.formFields.forEach((field) => {
        if (field.section === section.name) {
          const label = field.tr?.[lang] || field.label || ''
          let value: string | undefined = ''
          switch (field.type) {
            case 'radio': {
              const option = field.options.find((o) => o.value === data[field.name])
              value = option?.tr?.[lang] || option?.name || ''
              break
            }
            case 'checkBox': {
              value = data[field.name] === 'true' ? t('YES') : t('NO')
              break
            }
            case 'upload': {
              value = data[field.name]?.startsWith('$doc:')
                ? order.documents.find((d) => data[field.name].endsWith(d.id))?.fileName
                : data[field.name]?.split('|').at(0)
              break
            }
            default: {
              value = data[field.name]?.split(';base64')[0].split('|')[0]
              break
            }
          }
          rows.push([label, value ?? ''])
        }
      })
      autoTable(doc, {
        columnStyles: {
          0: { cellWidth: 40, fontStyle: 'italic', lineWidth: 0.3 },
          1: { lineWidth: 0.3 },
        },
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        startY: (doc as any).lastAutoTable.finalY,
        head: headers,
        body: rows,
      })
    })
    autoTable(doc, {
      columnStyles: {
        0: { cellWidth: 40, fontStyle: 'italic', lineWidth: 0.3 },
        1: { lineWidth: 0.3 },
      },
      head: headApprove,
      body: rowsApprove,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      startY: (doc as any).lastAutoTable.finalY,
      margin: 0,
    })
    autoTable(doc, {
      head: headApproveAdditional,
      body: rowsApproveAdditional,
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      startY: (doc as any).lastAutoTable.finalY,
      margin: 0,
      columnStyles: {
        0: { cellWidth: 40, fontStyle: 'italic', lineWidth: 0.3 },
        1: { cellWidth: 56, lineWidth: 0.3 },
        2: { cellWidth: 39, lineWidth: 0.3 },
        3: { lineWidth: 0.3 },
      },
      didParseCell: (tableData) => {
        if (rowsApproveAdditional.length === 0 && tableData.row.index === 0 && tableData.row.section === 'body') {
          tableData.cell.styles.fillColor = [255, 255, 255]
          tableData.cell.styles.lineColor = [255, 255, 255]
        }
      },
    })
    doc.save(`I4U_${this.getFormTitle(form, lang, true)}_${order.id}.pdf`)
  }
}
